// Quote win/loss modals (Session AAT, QUOTING DEPTH Loop 1).
//  - MarkQuoteLostModal: pick an active reason (+ optional note) and mark a quote lost.
//  - ManageLostReasonsModal: the reason-lookup CRUD (add / deactivate / delete), per the DELETE
//    RULE (a referenced reason cannot hard-delete, only deactivate). Both are admin/office.
import { useEffect, useState } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ApiError, api } from "../../lib/api";
import type { QuoteLostReason } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { friendlyApiError } from "../../lib/friendlyApiError";


// ---- Mark a quote lost -----------------------------------------------------
export function MarkQuoteLostModal({
  quoteId,
  quoteRef,
  onClose,
  onManageReasons,
  onMarked,
}: {
  quoteId: string;
  quoteRef: string;
  onClose: () => void;
  onManageReasons: () => void;
  onMarked: (ref: string) => void;
}) {
  const [reasons, setReasons] = useState<QuoteLostReason[] | null>(null);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.quoteLostReasons
      .list(true)
      .then((rs) => {
        if (!live) return;
        setReasons(rs);
        if (rs.length > 0) setReasonId(rs[0].id);
      })
      .catch(() => live && setError("Could not load the reasons. Please try again."));
    return () => {
      live = false;
    };
  }, []);

  async function confirm() {
    if (!reasonId) {
      setError("Choose a reason.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.quotes.markLost(quoteId, { lostReasonId: reasonId, note: note.trim() || undefined });
      onMarked(quoteRef);
    } catch (err) {
      setError(friendlyApiError(err, "Could not mark the quote lost. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const noReasons = reasons != null && reasons.length === 0;

  return (
    <ModalShell ariaLabel={`Mark quote ${quoteRef} lost`} onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-md" panelClassName="p-5 shadow-xl">
        <h2 className="font-heading text-lg text-ele-danger">Mark quote {quoteRef} lost</h2>
        <p className="mt-2 text-sm text-ele-body">Record why this quote did not convert. This is office bookkeeping only: it sends nothing to the customer.</p>

        {reasons == null ? (
          <p className="mt-4 text-sm text-ele-body">Loading reasons...</p>
        ) : noReasons ? (
          <p className="mt-4 text-sm text-ele-body">
            No reasons set up yet.{" "}
            <button type="button" onClick={onManageReasons} className="text-ele-blue underline hover:text-ele-strong">
              Add a reason
            </button>{" "}
            first.
          </p>
        ) : (
          <>
            <label className="mt-4 mb-1 block text-sm text-ele-body" htmlFor="lost-reason">Reason</label>
            <select
              id="lost-reason"
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            >
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>

            <label className="mt-3 mb-1 block text-sm text-ele-body" htmlFor="lost-note">Note (optional)</label>
            <textarea
              id="lost-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Any detail worth keeping (optional)"
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />
            <button type="button" onClick={onManageReasons} className="mt-2 text-xs text-ele-blue underline hover:text-ele-strong">
              Manage reasons
            </button>
          </>
        )}

        {error && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

        <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button variant="danger" onClick={confirm} disabled={busy || noReasons || !reasonId} className={MODAL_ACTION_BTN}>
            {busy ? "Marking..." : "Mark lost"}
          </Button>
        </div>
    </ModalShell>
  );
}

// ---- Manage the reason lookup ----------------------------------------------
export function ManageLostReasonsModal({ onClose }: { onClose: () => void }) {
  const [reasons, setReasons] = useState<QuoteLostReason[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoadError(false);
    try {
      setReasons(await api.quoteLostReasons.list());
    } catch {
      setLoadError(true);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function add() {
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    setError(null);
    try {
      await api.quoteLostReasons.create(label);
      setNewLabel("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.code === "duplicate" ? "That reason already exists." : "Could not add the reason. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  async function toggleActive(r: QuoteLostReason) {
    setBusyId(r.id);
    setError(null);
    try {
      await api.quoteLostReasons.update(r.id, { active: !r.active });
      await load();
    } catch {
      setError("Could not update the reason. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(r: QuoteLostReason) {
    setBusyId(r.id);
    setError(null);
    try {
      await api.quoteLostReasons.delete(r.id);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "in_use"
          ? `"${r.label}" is used by ${r.quoteCount} quote${r.quoteCount === 1 ? "" : "s"}. Deactivate it instead.`
          : "Could not delete the reason. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <ModalShell ariaLabel="Manage quote lost reasons" onClose={onClose} closeOnBackdrop width="max-w-md" panelClassName="p-5 shadow-xl">
        <h2 className="font-heading text-lg text-ele-blue">Quote lost reasons</h2>
        <p className="mt-1 text-sm text-ele-body">The reasons the office can pick when marking a quote lost. Deactivate a reason to hide it from the picker while keeping it on past quotes; a reason with no quotes can be deleted.</p>

        <div className="mt-4 flex gap-2">
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void add()}
            maxLength={80}
            placeholder="e.g. Price too high"
            className="flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
          <Button size="sm" onClick={add} disabled={adding || newLabel.trim() === ""}>
            {adding ? "Adding..." : "Add"}
          </Button>
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-ele-danger">{error}</p>}

        <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
          {loadError ? (
            <p role="alert" className="text-sm text-ele-strong">Could not load the reasons.</p>
          ) : reasons == null ? (
            <Loader shape="inline" />
          ) : reasons.length === 0 ? (
            <p className="text-sm text-ele-body">No reasons yet. Add the first one above.</p>
          ) : (
            reasons.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-ele border border-ele-purple-accent px-3 py-2">
                <div className="min-w-0">
                  <span className={`text-sm ${r.active ? "text-ele-strong" : "text-ele-body line-through opacity-70"}`}>{r.label}</span>
                  <span className="ml-2 text-xs text-ele-body">{r.quoteCount} quote{r.quoteCount === 1 ? "" : "s"}</span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(r)}
                    disabled={busyId === r.id}
                    className="text-xs text-ele-blue underline hover:text-ele-strong disabled:opacity-50"
                  >
                    {r.active ? "Deactivate" : "Reactivate"}
                  </button>
                  <ConfirmButton
                    onConfirm={() => remove(r)}
                    busy={busyId === r.id}
                    disabled={busyId === r.id}
                    className="text-xs text-ele-body hover:text-ele-danger"
                    label="Delete"
                    title={r.quoteCount > 0 ? "Used by quotes: deactivate instead" : "Delete this reason"}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-ele border border-ele-purple-accent px-4 py-2 text-sm">
            Done
          </button>
        </div>
    </ModalShell>
  );
}
